import { Modal as ModalContainer, Box, Typography, Grid } from '@mui/material';
import styled from '@emotion/styled';
import {
    SecondaryButtonRegular,
    PrimaryButton
} from '@/components/shared/Button';
import { useTranslation } from 'react-i18next';
import {
    FONT_WEIGHT,
    TEXT_SIZE,
    Title,
    SubTitle
} from '@/typography/Typography';
import { RadioButton } from '@/components/shared/RadioButton';

const ConfirmationBox = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 400px;
    height: auto;
    background-color: #ffffff;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 5px 20px;
    outline: none;
`;

export const OptionsBox = styled(Grid)`
    border: 1px solid #eaecf0;
    display: flex;
    padding: 16px;
    border-radius: 8px;
    width: 350px;
    justify-content: space-between;
    margin: 8px 0px;
    cursor: pointer;
`;

export const PrimaryButtonStyle = styled(PrimaryButton)`
    width: 170px;
    height: 44px !important;
    padding: 10px 18px !important;
`;

export const SecondaryButtonStyle = styled(SecondaryButtonRegular)`
    width: 170px;
    height: 44px !important;
    padding: 10px 18px !important;
`;

export interface IntegrationOption {
    id: string;
    icon: any;
    name: string | null;
    description?: string | null;
    selected: boolean;
}

interface IntegrationModalProps {
    dialogOpen: boolean;
    title: string;
    content?: JSX.Element | string;
    showCancelButton?: boolean;
    handleCancel?: () => void;
    handleContinue: () => void;
    options?: IntegrationOption[];
    selectedOptionId: string;
    setSelectedOptionId: (id: string) => void;
    url?: any;
    useUrl?: boolean;
}

const ModalHeading = ({ label }: { label: string }) => {
    return (
        <Typography
            fontWeight={FONT_WEIGHT.BOLD}
            fontSize={TEXT_SIZE.MEDIUM_SMALL}
            m={1}
        >
            {label}
        </Typography>
    );
};

const ModalDescription = ({ label }: { label: JSX.Element | string }) => {
    return (
        <Typography
            fontWeight={FONT_WEIGHT.REGULAR}
            fontSize={TEXT_SIZE.XS}
            color="text.secondary"
        >
            {label}
        </Typography>
    );
};

export const IntegrationDialog = (props: IntegrationModalProps) => {
    const { t } = useTranslation();

    const handleOptionSelect = (id: string) => {
        props.setSelectedOptionId(id);
    };

    return (
        <>
            <ModalContainer
                open={props.dialogOpen}
                onClose={props.handleCancel}
            >
                <ConfirmationBox>
                    <Box textAlign={'center'} marginTop={'10px'}>
                        <ModalHeading label={props.title} />
                        {props.content && (
                            <ModalDescription label={props.content} />
                        )}
                    </Box>

                    <Box>
                        {props.options?.map((option, index) => (
                            <OptionsBox
                                container
                                key={index}
                                style={{
                                    backgroundColor:
                                        option.id === props.selectedOptionId
                                            ? '#EFF8FF'
                                            : '#ffffff',
                                    border:
                                        option.id === props.selectedOptionId
                                            ? '1px solid #84CAFF'
                                            : '1px solid #EAECF0',
                                    alignItems: 'center'
                                }}
                                onClick={() => handleOptionSelect(option.id)}
                            >
                                <Grid item xs={10.5} display={'flex'}>
                                    <Grid item xs={2.5} display={'flex'}>
                                        {option.icon}
                                    </Grid>
                                    <Grid
                                        item
                                        xs={10.5}
                                        display={'block'}
                                        margin={'auto'}
                                    >
                                        <Title>{option.name}</Title>
                                        <SubTitle fontSize={'12px'}>
                                            {option.description}
                                        </SubTitle>
                                    </Grid>
                                </Grid>
                                <Grid item xs={1} justifyItems={'flex-end'}>
                                    <RadioButton
                                        checked={
                                            option.id === props.selectedOptionId
                                        }
                                    />
                                </Grid>
                            </OptionsBox>
                        ))}
                    </Box>

                    <Grid m={2} container justifyContent={'space-around'}>
                        {props.showCancelButton !== false && (
                            <SecondaryButtonStyle
                                onClick={props.handleCancel}
                                sx={{ width: '170px' }}
                            >
                                {t('close')}
                            </SecondaryButtonStyle>
                        )}
                        {props.useUrl ? (
                            <PrimaryButtonStyle>
                                <a
                                    href={props.url}
                                    style={{
                                        color: 'white',
                                        textDecoration: 'none'
                                    }}
                                >
                                    {t('continue')}
                                </a>
                            </PrimaryButtonStyle>
                        ) : (
                            <PrimaryButtonStyle onClick={props.handleContinue}>
                                {t('continue')}
                            </PrimaryButtonStyle>
                        )}
                    </Grid>
                </ConfirmationBox>
            </ModalContainer>
        </>
    );
};
